import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchPrintBatches,
  createPrintBatch,
  fetchBatchBadges,
  pairNfcBadge,
  importNfcPairingCsv,
  PrintBatchRecord,
  Badge4x6Data,
  API_BASE,
} from '../lib/api';
import { BadgeCard4x6 } from './BadgeCard4x6';
import {
  Printer,
  Radio,
  FileSpreadsheet,
  Plus,
  RefreshCw,
  Search,
  CheckCircle2,
  AlertTriangle,
  Upload,
  X,
  Sparkles,
  Download,
  Calendar,
  Layers,
  ArrowRight,
  ShieldCheck,
  Check,
  RotateCw,
} from 'lucide-react';

interface AdminBadgeProductionProps {
  adminKey: string;
}

export function AdminBadgeProduction({ adminKey }: AdminBadgeProductionProps) {
  const [activeTab, setActiveTab] = useState<'batches' | 'pairing'>('batches');
  const [pairingMode, setPairingMode] = useState<'live' | 'csv'>('live');

  // Batch Creation State
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [batchName, setBatchName] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterTier, setFilterTier] = useState('all');
  const [filterCompany, setFilterCompany] = useState('');
  const [onlyUnprinted, setOnlyUnprinted] = useState(true);

  // Viewing / Printing a specific batch
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);

  // Live NFC Pairing Station State
  const [badgeScanCode, setBadgeScanCode] = useState('');
  const [nfcTagInput, setNfcTagInput] = useState('');
  const [pairOutcome, setPairOutcome] = useState<{
    success: boolean;
    message: string;
    attendee?: any;
    theme?: any;
    badgeCode?: string;
    nfcTagId?: string;
  } | null>(null);

  // CSV Import State
  const [csvContent, setCsvContent] = useState('');
  const [csvOutcome, setCsvOutcome] = useState<{
    totalRows: number;
    pairedCount: number;
    errors?: string[];
  } | null>(null);

  const queryClient = useQueryClient();
  const nfcInputRef = useRef<HTMLInputElement | null>(null);
  const badgeInputRef = useRef<HTMLInputElement | null>(null);

  // 1. Fetch Print Batches
  const {
    data: batchData,
    isLoading: isBatchesLoading,
    refetch: refetchBatches,
    isFetching: isBatchesFetching,
  } = useQuery({
    queryKey: ['admin-print-batches', adminKey],
    queryFn: () => fetchPrintBatches(adminKey),
    enabled: !!adminKey,
  });

  const batches: PrintBatchRecord[] = batchData?.batches || [];

  // 2. Fetch Badges for Selected Batch
  const {
    data: selectedBatchData,
    isLoading: isBadgesLoading,
  } = useQuery({
    queryKey: ['admin-batch-badges', adminKey, selectedBatchId],
    queryFn: () => fetchBatchBadges(selectedBatchId!, adminKey),
    enabled: !!adminKey && !!selectedBatchId,
  });

  const activeBadges: Badge4x6Data[] = selectedBatchData?.badges || [];

  // 3. Create Batch Mutation
  const createBatchMutation = useMutation({
    mutationFn: () =>
      createPrintBatch(
        {
          name: batchName || `Batch ${new Date().toLocaleDateString()}`,
          role: filterRole !== 'all' ? filterRole : undefined,
          tier: filterTier !== 'all' ? filterTier : undefined,
          company: filterCompany.trim() || undefined,
          onlyUnprinted,
        },
        adminKey
      ),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-print-batches'] });
      setIsCreateOpen(false);
      setBatchName('');
      setSelectedBatchId(data.batch.id);
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to create print batch');
    },
  });

  // 4. Pair NFC Mutation
  const pairNfcMutation = useMutation({
    mutationFn: (payload: { badgeCode: string; nfcTagId: string }) =>
      pairNfcBadge(payload, adminKey),
    onSuccess: (data) => {
      setPairOutcome({
        success: true,
        message: data.message,
        attendee: data.attendee,
        theme: data.theme,
        badgeCode: data.badgeCode,
        nfcTagId: data.nfcTagId,
      });
      setBadgeScanCode('');
      setNfcTagInput('');
      badgeInputRef.current?.focus();
    },
    onError: (err: any) => {
      setPairOutcome({
        success: false,
        message: err.message || 'Failed to pair NFC tag',
      });
    },
  });

  // 5. Import NFC CSV Mutation
  const importCsvMutation = useMutation({
    mutationFn: () => importNfcPairingCsv({ csvContent }, adminKey),
    onSuccess: (data) => {
      setCsvOutcome(data);
      setCsvContent('');
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to import NFC CSV');
    },
  });

  const handlePairSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!badgeScanCode.trim() || !nfcTagInput.trim()) return;
    pairNfcMutation.mutate({
      badgeCode: badgeScanCode.trim(),
      nfcTagId: nfcTagInput.trim(),
    });
  };

  const downloadCsv = (batchId: string, batchNumber: string) => {
    window.open(`${API_BASE}/api/production/batches/${batchId}/export-csv?token=${encodeURIComponent(adminKey)}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xs">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="p-1.5 rounded-lg bg-[#0A3D27]/10 text-[#0A3D27]">
              <Printer className="w-4 h-4" />
            </span>
            <h3 className="text-base font-black text-slate-900">4×6 Badge Production & NFC Pairing Station</h3>
          </div>
          <p className="text-xs text-slate-500">
            Generate on-demand print batches (4×6 printable layout & vendor CSV exports) and pair NTAG215 hardware stickers.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {activeTab === 'batches' ? (
            <button
              onClick={() => setIsCreateOpen(true)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Create Print Batch</span>
            </button>
          ) : null}

          <button
            onClick={() => refetchBatches()}
            disabled={isBatchesFetching}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition-colors cursor-pointer border border-slate-200"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isBatchesFetching ? 'animate-spin text-[#0A3D27]' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Sub-Tabs: Batches vs NFC Station */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          type="button"
          onClick={() => setActiveTab('batches')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'batches'
              ? 'bg-[#0A3D27] text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          <span>On-Demand Print Batches ({batches.length})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('pairing')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            activeTab === 'pairing'
              ? 'bg-[#0A3D27] text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Radio className="w-3.5 h-3.5" />
          <span>NFC Hardware Pairing Station</span>
        </button>
      </div>

      {/* ================= TAB 1: PRINT BATCHES ================= */}
      {activeTab === 'batches' && (
        <div className="space-y-6">
          {/* Batches Roster Table */}
          <div className="bg-white border border-slate-200/90 rounded-2xl overflow-hidden shadow-xs">
            {isBatchesLoading ? (
              <div className="p-12 text-center text-slate-500 text-xs flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin text-[#0A3D27]" />
                <span>Loading print batches...</span>
              </div>
            ) : batches.length === 0 ? (
              <div className="p-12 text-center text-slate-500 space-y-3">
                <Layers className="w-8 h-8 mx-auto text-slate-400 mb-1" />
                <p className="text-sm font-semibold text-slate-900">No print batches created yet</p>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Click "Create Print Batch" above to bundle unprinted attendees by role or company into print-ready 4×6 sheets and vendor CSVs.
                </p>
                <button
                  onClick={() => setIsCreateOpen(true)}
                  className="px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold cursor-pointer"
                >
                  Create First Batch
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                      <th className="py-3 px-4">Batch Number & Name</th>
                      <th className="py-3 px-4">Filter Criteria</th>
                      <th className="py-3 px-4">Total Badges</th>
                      <th className="py-3 px-4">Created Date</th>
                      <th className="py-3 px-4 text-right">Production Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-700">
                    {batches.map((batch) => (
                      <tr key={batch.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3.5 px-4">
                          <div className="space-y-0.5">
                            <span className="font-mono font-bold text-xs text-[#0A3D27] bg-[#0A3D27]/10 px-2 py-0.5 rounded border border-[#0A3D27]/20">
                              {batch.batchNumber}
                            </span>
                            <p className="font-bold text-slate-900 text-sm mt-1">{batch.name}</p>
                          </div>
                        </td>

                        <td className="py-3.5 px-4 text-slate-600">
                          <div className="space-y-0.5 text-[11px]">
                            {batch.filterCriteria?.role && (
                              <p>Role: <span className="font-semibold text-slate-800 capitalize">{batch.filterCriteria.role}</span></p>
                            )}
                            {batch.filterCriteria?.company && (
                              <p>Company: <span className="font-semibold text-slate-800">{batch.filterCriteria.company}</span></p>
                            )}
                            {batch.filterCriteria?.onlyUnprinted && (
                              <span className="text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">
                                Unprinted Only
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-black text-slate-900">{batch.totalCount}</span>
                            <span className="text-[10px] text-slate-500 uppercase">badges</span>
                          </div>
                        </td>

                        <td className="py-3.5 px-4 text-slate-500">
                          <div>
                            <span>{new Date(batch.createdAt).toLocaleDateString()}</span>
                            <span className="text-[10px] block font-mono text-slate-400">
                              by {batch.createdBy || 'Staff'}
                            </span>
                          </div>
                        </td>

                        <td className="py-3.5 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setSelectedBatchId(batch.id)}
                              className="px-3 py-1.5 bg-[#FAF5EA] hover:bg-[#F3E8CE] text-[#785613] border border-[#E6C687] rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                            >
                              <Printer className="w-3.5 h-3.5" />
                              <span>View 4×6 Badges</span>
                            </button>

                            <button
                              onClick={() => downloadCsv(batch.id, batch.batchNumber)}
                              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                              title="Download vendor data CSV"
                            >
                              <Download className="w-3.5 h-3.5 text-[#0A3D27]" />
                              <span>Vendor CSV</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================= TAB 2: NFC PAIRING STATION ================= */}
      {activeTab === 'pairing' && (
        <div className="space-y-6">
          {/* Mode Switcher */}
          <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="p-1.5 rounded-lg bg-[#0A3D27]/10 text-[#0A3D27]">
                <Radio className="w-4 h-4" />
              </span>
              <div>
                <h4 className="text-xs font-bold text-slate-900">NTAG215 Hardware Tag Linking</h4>
                <p className="text-[11px] text-slate-500">
                  Method A (USB scanner + USB NFC tap) or Method C (Supplier bulk CSV import).
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl">
              <button
                type="button"
                onClick={() => setPairingMode('live')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  pairingMode === 'live'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Live Pairing Station
              </button>
              <button
                type="button"
                onClick={() => setPairingMode('csv')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  pairingMode === 'csv'
                    ? 'bg-white text-slate-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Supplier CSV Import
              </button>
            </div>
          </div>

          {pairingMode === 'live' ? (
            /* Live USB Barcode Scanner + USB NFC Tap Interface */
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-6 bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-5">
                <div className="border-b border-slate-200 pb-3">
                  <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                    <Radio className="w-4 h-4 text-[#0A3D27]" />
                    <span>Station Input (Pair printed badge to NFC sticker)</span>
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Step 1: Scan badge QR code → Step 2: Stick tag & tap NFC reader.
                  </p>
                </div>

                <form onSubmit={handlePairSubmit} className="space-y-4 text-xs">
                  <div className="space-y-1">
                    <label className="font-bold text-slate-700 flex items-center justify-between">
                      <span>1. Scanned Badge Code *</span>
                      <span className="text-[10px] text-slate-400 font-mono">Barcode Reader</span>
                    </label>
                    <input
                      ref={badgeInputRef}
                      type="text"
                      required
                      value={badgeScanCode}
                      onChange={(e) => setBadgeScanCode(e.target.value)}
                      placeholder="e.g. IIAB27-VIP-879102"
                      className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 font-mono text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="font-bold text-slate-700 flex items-center justify-between">
                      <span>2. NFC Hardware Tag UID *</span>
                      <span className="text-[10px] text-slate-400 font-mono">USB Tap / Web NFC</span>
                    </label>
                    <input
                      ref={nfcInputRef}
                      type="text"
                      required
                      value={nfcTagInput}
                      onChange={(e) => setNfcTagInput(e.target.value)}
                      placeholder="e.g. 04:5A:82:1A:6B:40:80"
                      className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3.5 py-2.5 font-mono text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={pairNfcMutation.isPending || !badgeScanCode.trim() || !nfcTagInput.trim()}
                    className="w-full py-3 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl font-bold transition-all shadow-xs cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {pairNfcMutation.isPending ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4" />
                    )}
                    <span>Link & Confirm Hardware Tag</span>
                  </button>
                </form>
              </div>

              {/* Station Outcome Verdict Card */}
              <div className="lg:col-span-6 bg-white border border-slate-200 rounded-3xl p-6 shadow-xs flex flex-col justify-center min-h-[260px]">
                {pairOutcome ? (
                  <div
                    className={`rounded-2xl p-5 border text-center space-y-3 animate-in zoom-in-95 duration-150 ${
                      pairOutcome.success
                        ? 'bg-emerald-50 border-emerald-300'
                        : 'bg-rose-50 border-rose-300'
                    }`}
                  >
                    <div
                      className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto border ${
                        pairOutcome.success
                          ? 'bg-emerald-100 text-[#0A3D27] border-emerald-300'
                          : 'bg-rose-100 text-rose-700 border-rose-300'
                      }`}
                    >
                      {pairOutcome.success ? (
                        <ShieldCheck className="w-7 h-7" />
                      ) : (
                        <AlertTriangle className="w-7 h-7" />
                      )}
                    </div>

                    <div className="space-y-1">
                      <span className="text-[10px] font-black uppercase tracking-wider bg-white px-2.5 py-0.5 rounded-full border shadow-xs text-slate-800">
                        {pairOutcome.success ? 'NFC Sticker Paired' : 'Pairing Error'}
                      </span>
                      <h4 className="text-lg font-black text-slate-900">
                        {pairOutcome.attendee?.fullName || 'Hardware Linking'}
                      </h4>
                      <p className="text-xs text-slate-600">{pairOutcome.message}</p>
                    </div>

                    {pairOutcome.success && (
                      <div className="bg-white p-2.5 rounded-xl border border-emerald-200 text-left text-[11px] font-mono space-y-0.5">
                        <p><span className="text-slate-400">Badge Code:</span> <span className="font-bold text-slate-800">{pairOutcome.badgeCode}</span></p>
                        <p><span className="text-slate-400">NFC UID:</span> <span className="font-bold text-[#0A3D27]">{pairOutcome.nfcTagId}</span></p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8 space-y-2 text-slate-400">
                    <Radio className="w-10 h-10 text-slate-300 mx-auto" />
                    <p className="text-xs font-semibold text-slate-600">Awaiting Badge & Tag Scan</p>
                    <p className="text-[10px] text-slate-400 max-w-xs mx-auto">
                      Scan the printed 4×6 badge QR, then place the NTAG215 sticker on the USB reader. Takes ~5 seconds per pass.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Method C: Supplier Bulk CSV Import */
            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xs space-y-4 max-w-2xl">
              <div className="border-b border-slate-200 pb-3">
                <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-[#0A3D27]" />
                  <span>Supplier Tag UID Import</span>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Paste CSV from the print vendor matching Badge Code to NFC Tag UID (`badgeCode,nfcTagId`).
                </p>
              </div>

              <div className="space-y-2 text-xs">
                <label className="font-bold text-slate-700">Paste CSV Contents</label>
                <textarea
                  rows={8}
                  value={csvContent}
                  onChange={(e) => setCsvContent(e.target.value)}
                  placeholder={`badgeCode,nfcTagId\nIIAB27-VIP-8721,04:AA:BB:CC:DD:EE:01\nIIAB27-EXH-4412,04:AA:BB:CC:DD:EE:02`}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl p-3 font-mono text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <button
                type="button"
                onClick={() => importCsvMutation.mutate()}
                disabled={importCsvMutation.isPending || !csvContent.trim()}
                className="px-5 py-2.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl font-bold transition-all shadow-xs cursor-pointer flex items-center gap-2 disabled:opacity-50 text-xs"
              >
                {importCsvMutation.isPending && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                <span>Import & Link Hardware UIDs</span>
              </button>

              {csvOutcome && (
                <div className="p-3.5 bg-emerald-50 border border-emerald-300 rounded-xl text-xs space-y-1">
                  <p className="font-bold text-[#0A3D27]">Import Complete!</p>
                  <p className="text-slate-700">
                    Processed <span className="font-bold">{csvOutcome.totalRows}</span> rows • Successfully linked{' '}
                    <span className="font-bold text-[#0A3D27]">{csvOutcome.pairedCount}</span> hardware tags.
                  </p>
                  {csvOutcome.errors && csvOutcome.errors.length > 0 && (
                    <div className="mt-2 text-rose-700 text-[11px] font-mono space-y-0.5">
                      <p className="font-bold">Errors ({csvOutcome.errors.length}):</p>
                      {csvOutcome.errors.slice(0, 5).map((err, i) => (
                        <p key={i}>• {err}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ================= MODAL: CREATE PRINT BATCH ================= */}
      {isCreateOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div className="flex items-center gap-2">
                <span className="p-1.5 rounded-lg bg-[#0A3D27]/10 text-[#0A3D27]">
                  <Printer className="w-4 h-4" />
                </span>
                <h3 className="text-base font-black text-slate-900">Create Badge Print Batch</h3>
              </div>
              <button
                onClick={() => setIsCreateOpen(false)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                createBatchMutation.mutate();
              }}
              className="space-y-3.5 text-xs"
            >
              <div className="space-y-1">
                <label className="font-bold text-slate-700">Batch Name *</label>
                <input
                  type="text"
                  required
                  value={batchName}
                  onChange={(e) => setBatchName(e.target.value)}
                  placeholder="e.g. VIP & Speaker Advance Print"
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-slate-700">Filter Role</label>
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  >
                    <option value="all">All Roles</option>
                    <option value="vip">VIP</option>
                    <option value="speaker">Speaker</option>
                    <option value="exhibitor">Exhibitor</option>
                    <option value="delegate">Delegate</option>
                    <option value="conference_delegate">Conference Delegate</option>
                    <option value="trade_visitor">Trade Visitor</option>
                    <option value="media">Media</option>
                    <option value="organiser">Organiser</option>
                    <option value="student_innovator">Student Innovator</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="font-bold text-slate-700">Filter Tier</label>
                  <select
                    value={filterTier}
                    onChange={(e) => setFilterTier(e.target.value)}
                    className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  >
                    <option value="all">All Tiers</option>
                    <option value="vip">VIP Pass</option>
                    <option value="conference_delegate">Delegate Pass</option>
                    <option value="trade_visitor">Trade Visitor</option>
                    <option value="exhibitor">Exhibitor</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700">Filter Company (Optional)</label>
                <input
                  type="text"
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  placeholder="e.g. Bharat Agri"
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <label className="flex items-center gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onlyUnprinted}
                  onChange={(e) => setOnlyUnprinted(e.target.checked)}
                  className="w-4 h-4 text-[#0A3D27] rounded focus:ring-[#0A3D27]"
                />
                <span className="font-semibold text-slate-700">
                  Include only unprinted attendees (skip already printed)
                </span>
              </label>

              <div className="border-t border-slate-200 pt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsCreateOpen(false)}
                  className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold cursor-pointer border border-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createBatchMutation.isPending}
                  className="px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl font-bold transition-all shadow-xs cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                >
                  {createBatchMutation.isPending && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  <span>Generate Batch</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ================= MODAL: 4x6 MULTI-PAGE PRINT SHEET ================= */}
      {selectedBatchId && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl max-w-4xl w-full p-6 space-y-6 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-200 pb-4 print:hidden">
              <div className="flex items-center gap-2">
                <Printer className="w-5 h-5 text-[#0A3D27]" />
                <div>
                  <h3 className="text-base font-black text-slate-900">
                    4×6 Physical Badge Print Preview ({activeBadges.length} Badges)
                  </h3>
                  <p className="text-xs text-slate-500">
                    Standard 4in × 6in physical layout with high-contrast typography, QR code, and NFC sticker mark.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.print()}
                  className="px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <Printer className="w-4 h-4" />
                  <span>Send {activeBadges.length} Badges to Printer</span>
                </button>

                <button
                  onClick={() => setSelectedBatchId(null)}
                  className="text-slate-400 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Print Sheet Grid */}
            {isBadgesLoading ? (
              <div className="p-12 text-center text-slate-500 text-xs flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin text-[#0A3D27]" />
                <span>Loading batch badge layouts...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-items-center print:block print:p-0">
                {activeBadges.map((badgeItem) => (
                  <div key={badgeItem.id} className="print:break-after-page print:m-0">
                    <BadgeCard4x6 badge={badgeItem} allowFlip={true} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminBadgeProduction;
